import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthenticationService } from "app/auth/service";
import { UtilsService } from "app/auth/service/utils.service";
import { IProductForm } from "app/modules/products/models/products";
import { ProductsService } from "app/modules/products/products.service";

@Component({
  selector: "app-product-form",
  templateUrl: "./product-form.component.html",
  styleUrls: ["./product-form.component.scss"],
})
export class ProductFormComponent implements OnInit {
  @ViewChild("productForm") productForm;
  @Input() selectedProduct: IProductForm;
  @Output() updateList = new EventEmitter<any>();
  public showErrors: boolean = false;
  public attachments_list: Array<any> = [];
  public attachments_list_obj: Array<any> = [];
  public formGroup: FormGroup;
  constructor(
    public _modalService: NgbModal,
    public _utilService: UtilsService,
    public _productsService: ProductsService,
    private router: Router,
    private formBuilder: FormBuilder,
    public _authenticationService: AuthenticationService,
  ) {
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required]],
      monthly_cost: [null, [Validators.required]],
      monthly_amount: [null, [Validators.required]],
      description: [null, [Validators.maxLength(255)]],
    });
  }
  async ngOnInit() {
    setTimeout(() => {
      this._modalService.open(this.productForm, {
        centered: true,
        size: "md",
        backdrop: false,
        keyboard: false,
      });
    });
    if (!!this.selectedProduct) {
      this.formGroup.patchValue({
        name: this.selectedProduct.name ? this.selectedProduct.name : null,
        monthly_cost: this.selectedProduct.monthly_cost ? this._utilService.addDotsToNumber(this.selectedProduct.monthly_cost) : null,
        monthly_amount: this.selectedProduct.monthly_amount ? this._utilService.addDotsToNumber(this.selectedProduct.monthly_amount) : null,
        description: this.selectedProduct.description ? this.selectedProduct.description : null,
      });
    }
  }

  formHasError(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroup.controls[field_name].status === "INVALID";
    } else {
      return this.formGroup.controls[field_name].status === "INVALID" && this.formGroup.controls[field_name].touched;
    }
  }

  formatMoneyValue(type: string) {
    if (type === "cost") {
      this.formGroup.patchValue({ monthly_cost: this._utilService.addDotsToNumber(this.formGroup.value.monthly_cost) });
    } else {
      this.formGroup.patchValue({ monthly_amount: this._utilService.addDotsToNumber(this.formGroup.value.monthly_amount) });
    }
  }

  modalClose() {
    this._modalService.dismissAll();
    this._utilService.popupOpenProductForm = !this._utilService.popupOpenProductForm;
    this.showErrors = false;
    this.attachments_list_obj = [];
    this.attachments_list = [];
    this.selectedProduct = null;
  }

  async addAttachmentToList(files: any) {
    // I create a variable from the actual list
    let updatedList: any[] = this.attachments_list_obj.filter((item: any) => item.name !== "");
    //For all files charged in the queue
    for (let file of files) {
      //check dimensions
      if (file.size > 10000000) {
        await this._utilService.showSWALError({ text: "ERROR_MAX_FILE_SIZE" });
        continue;
      }
      //If exits in the list I'll remove it
      const exists: any = updatedList.find((item: any) => item.name === file.name);
      // If exists remove it
      if (exists) {
        updatedList = updatedList.find((item: any) => item.name === file.name);
      }
      //If it does not exist I'll add it
      else {
        this.attachments_list.push(file);
      }
      //Add to updated list
      updatedList.push({
        name: file.name,
        size: this._utilService.formatBytes(file.size),
        status: 0,
      });
    }
    //I update the variable for the page
    this.attachments_list_obj = updatedList;
  }

  resetAttachmentsList() {
    this.attachments_list_obj = [];
    this.attachments_list = [];
  }

  removeAttachmentFromList(index: any) {
    this.attachments_list_obj = this.attachments_list_obj.filter((item: any, i: number) => i !== index);
    this.attachments_list = this.attachments_list.filter((item: any, i: number) => i !== index);
  }

  async uploadAttachments(providerCostId: number) {
    // I'll wait for all attachments to be uploaded
    await Promise.all(
      this.attachments_list.map((file: File) => {
        return this._productsService.uploadAttachmentProduct(file, providerCostId);
      }),
    ).catch(async () => {
      await this._utilService.showSWALError({ text: "ERROR_UPLOAD_FILE" });
    });
  }

  async deleteAttachment(id: number) {
    await this._utilService
      .showSWAL({
        title: "CONFIRM_DELETE_FILE",
        icon: "error",
        confirmButtonText: "CONFIRM_DELETE_BTN",
        customClass: {
          confirmButton: "btn btn-danger",
        },
      })
      .then(async (result) => {
        if (result.value) {
          await this._productsService
            .deleteAttachmentProduct(id)
            .then(async () => {
              this.selectedProduct.attachments = this.selectedProduct.attachments.filter((item: any) => item.id !== id);
              await this._utilService.showSWALSuccess({ text: "SUCCESS_DELETE_FILE" });
            })
            .catch(() => this._utilService.showSWALError({ text: "ERROR_DELETE_FILE" }));
        } else if (result.isDismissed) {
          await this._utilService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT",
          });
        }
      });
  }

  async createProduct() {
    if (!this._authenticationService.hasCapability("create_product")) {
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    if (this.formGroup.invalid) {
      await this._utilService.showSWALError({ text: "COMPILE_ALL_FIELD" });
      return false;
    }
    this._utilService.showLoading();
    await this._productsService
      .createProduct(this.formGroup.value)
      .then(async (res: IProductForm) => {
        this.modalClose();
        if (this.attachments_list && this.attachments_list.length > 0) {
          this._utilService.hideLoading();
          await this.uploadAttachments(res.id);
        }
        this.updateList.emit();
        await this._productsService.getProductsList();
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_CREATE_PRODUCT" });
      })
      .catch((err: any) => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_CREATE_PRODUCT" });
        console.error(err);
      });
  }

  async editProduct() {
    if (!this._authenticationService.hasCapability("edit_product")) {
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    if (this.formGroup.invalid) {
      await this._utilService.showSWALError({ text: "COMPILE_ALL_FIELD" });
      return false;
    }
    this._utilService.showLoading();
    await this._productsService
      .editProduct(this.selectedProduct.id, this.formGroup.value)
      .then(async () => {
        this.modalClose();
        if (this.attachments_list && this.attachments_list.length > 0) {
          this._utilService.hideLoading();
          await this.uploadAttachments(this.selectedProduct.id);
        }
        this.updateList.emit();
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_PRODUCT" });
      })
      .catch((err: any) => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_EDIT_PRODUCT" });
        console.error(err);
      });
  }
}
